import React from 'react';
import { Box, Typography, Link, IconButton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import { palette } from '../../theme/colors';

const footerLinks = {
  product: [
    { label: 'Browse Stores', path: '/stores' },
    { label: 'All Products', path: '/products' },
    { label: 'Shopping Cart', path: '/cart' },
    { label: 'Track Order', path: '/track-reservation' },
  ],
  company: [
    { label: 'About Us', path: '#' },
    { label: 'Careers', path: '#' },
    { label: 'Blog', path: '#' },
    { label: 'Press', path: '#' },
  ],
  support: [
    { label: 'Help Center', path: '#' },
    { label: 'Contact Us', path: '#' },
    { label: 'Privacy Policy', path: '#' },
    { label: 'Terms of Service', path: '#' },
  ],
};

export const Footer: React.FC = () => {
  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        pt: 6,
        pb: 4,
        px: 4,
        bgcolor: palette.surface,
        borderTop: `1px solid ${palette.border}`,
      }}
    >
      <Box
        sx={{
          maxWidth: 1400,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '2fr 1fr 1fr 1fr' },
          gap: 4,
        }}
      >
        {/* Brand Section */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: palette.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StorefrontOutlinedIcon sx={{ color: palette.textOnPrimary, fontSize: 22 }} />
            </Box>
            <Typography variant="h6" fontWeight={700}>
              Nexera
            </Typography>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 280, lineHeight: 1.7 }}
          >
            Your one-stop marketplace for discovering and reserving products from multiple stores.
            Shop smart, shop local.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[TwitterIcon, InstagramIcon, LinkedInIcon, GitHubIcon].map((Icon, index) => (
              <IconButton
                key={index}
                size="small"
                sx={{
                  bgcolor: palette.sky,
                  color: palette.textSecondary,
                  '&:hover': { bgcolor: palette.primary, color: palette.textOnPrimary },
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </IconButton>
            ))}
          </Box>
        </Box>

        {/* Product Links */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            Product
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {footerLinks.product.map((link) => (
              <Link
                key={link.label}
                component={RouterLink}
                to={link.path}
                sx={{
                  color: palette.textSecondary,
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: palette.textPrimary },
                }}
              >
                {link.label}
              </Link>
            ))}
          </Box>
        </Box>

        {/* Company Links */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            Company
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {footerLinks.company.map((link) => (
              <Link
                key={link.label}
                component={RouterLink}
                to={link.path}
                sx={{
                  color: palette.textSecondary,
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: palette.textPrimary },
                }}
              >
                {link.label}
              </Link>
            ))}
          </Box>
        </Box>

        {/* Support Links */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            Support
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {footerLinks.support.map((link) => (
              <Link
                key={link.label}
                component={RouterLink}
                to={link.path}
                sx={{
                  color: palette.textSecondary,
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: palette.textPrimary },
                }}
              >
                {link.label}
              </Link>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Bottom Bar */}
      <Box
        sx={{
          maxWidth: 1400,
          mx: 'auto',
          mt: 6,
          pt: 3,
          borderTop: `1px solid ${palette.border}`,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} Nexera. All rights reserved.
        </Typography>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Link
            href="#"
            sx={{
              color: palette.textSecondary,
              textDecoration: 'none',
              fontSize: '0.8rem',
              '&:hover': { color: palette.textPrimary },
            }}
          >
            Privacy
          </Link>
          <Link
            href="#"
            sx={{
              color: palette.textSecondary,
              textDecoration: 'none',
              fontSize: '0.8rem',
              '&:hover': { color: palette.textPrimary },
            }}
          >
            Terms
          </Link>
          <Link
            href="#"
            sx={{
              color: palette.textSecondary,
              textDecoration: 'none',
              fontSize: '0.8rem',
              '&:hover': { color: palette.textPrimary },
            }}
          >
            Cookies
          </Link>
        </Box>
      </Box>
    </Box>
  );
};
